/**
 * Shared box-shadow / drop-shadow builders for Image & Video elements.
 *
 * Mirrors the implementation in the static-library so the editor preview
 * and the published renderer produce identical shadows. Supports three
 * input shapes for color/opacity:
 *   - literal value (hex color or number) → baked in
 *   - `"design-system"` sentinel → read from CSS variables
 *   - `undefined` → falls back to the hardcoded default look
 *
 * The retro/flat style intentionally uses a fallback of `1` so the user's
 * opacity slider maps 1:1 to the alpha channel (100% = fully opaque solid
 * color). Blur keeps its per-layer alpha weights so the soft shadow
 * scales naturally.
 */

const hexToRgb = (hex: string): string => {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h[0] + h[0] + h[1] + h[1] + h[2] + h[2] : h;
  return `${Number.parseInt(full.slice(0, 2), 16)}, ${Number.parseInt(
    full.slice(2, 4),
    16,
  )}, ${Number.parseInt(full.slice(4, 6), 16)}`;
};

const toRgbTriplet = (color?: string): string => {
  if (!color) return "0, 0, 0";
  if (color.startsWith("#")) return hexToRgb(color);
  return color;
};

const resolveColor = (color?: string | "design-system"): string =>
  color === "design-system"
    ? "var(--media-shadow-color, 0, 0, 0)"
    : toRgbTriplet(color);

/**
 * When the entity has no `shadowOpacity` field at all (legacy story) OR
 * the design-system CSS variable is unset, fall back to the per-style
 * alpha that matches the pre-opacity-slider rendering:
 *   - retro: solid 0.2 alpha → at fallback=1 that's 20%
 *   - blur layers: their hardcoded base alphas → 100% of `fallback`
 * `legacyDefaultPercent` encodes that per call site.
 */
const resolveOpacity = (
  opacity: number | "design-system" | undefined,
  fallback: number,
  legacyDefaultPercent: number,
): string => {
  if (opacity === "design-system") {
    return `calc(var(--media-shadow-opacity, ${legacyDefaultPercent}) * ${fallback} / 100)`;
  }
  const actual = opacity != null ? opacity : legacyDefaultPercent;
  return String((actual / 100) * fallback);
};

export const buildMediaBoxShadow = (
  style: string,
  color?: string | "design-system",
  opacity?: number | "design-system",
): string => {
  const c = resolveColor(color);
  if (style === "blur") {
    return [
      `59px 127px 39px 0px rgba(${c}, 0)`,
      `38px 81px 36px 0px rgba(${c}, ${resolveOpacity(opacity, 0.01, 100)})`,
      `21px 46px 30px 0px rgba(${c}, ${resolveOpacity(opacity, 0.05, 100)})`,
      `9px 20px 22px 0px rgba(${c}, ${resolveOpacity(opacity, 0.09, 100)})`,
      `2px 5px 12px 0px rgba(${c}, ${resolveOpacity(opacity, 0.1, 100)})`,
    ].join(", ");
  }
  if (style === "retro") {
    // Single-layer flat shadow: opacity maps directly to alpha, so 100%
    // = fully opaque. Legacy default is 20% to preserve the old rgba(0,0,0,0.2).
    return `8px 8px 0px 0px rgba(${c}, ${resolveOpacity(opacity, 1, 20)})`;
  }
  return "none";
};

export const buildMediaDropShadow = (
  style: string,
  color?: string | "design-system",
  opacity?: number | "design-system",
): string => {
  const c = resolveColor(color);
  if (style === "blur") {
    return `drop-shadow(0px 4px 8px rgba(${c}, ${resolveOpacity(opacity, 0.3, 100)}))`;
  }
  if (style === "retro") {
    // See note in buildMediaBoxShadow.
    return `drop-shadow(8px 8px 0px rgba(${c}, ${resolveOpacity(opacity, 1, 20)}))`;
  }
  return "none";
};
